<template>
  <form v-if="!isLoading" novalidate @submit.prevent="updateQuote">
    <div class="columns onboarding-services-view--form">
      <div class="column is-8">
        <div v-if="isInstantQuote" class="onboarding-services-view--instant-quote-header">
          <h1>Instant Quote for</h1>
          <h1 class="onboarding-services-view--instant-quote-name">{{ serviceName }}</h1>
        </div>
        <h4 v-else class="onboarding-services-view--h2">{{ serviceName }}</h4>
        <div v-if="!isInstantQuote" class="ls-form-header">
          <h1 class="has-text-weight-bold">{{ selectedService.name }}</h1>
          <LsTypicalPricing
            v-if="showPriceRange"
            test-id="typical_pricing"
            :market-id="marketID"
            :service-id="service_id"
            class="ls-typical-pricing"
          />
          <LsClickable
            track-id="order_services_form-how_quoting_works"
            class="ls-how-quoting-works"
            @click="howQuotingWorks"
          >
            <div class="ls-question-circle">?</div>
            <span>See how quoting works</span>
          </LsClickable>
        </div>
        <OnboardingInstantQuoteBushTrimmingForm
          v-if="selectedService.type === 'instant_quote_bush_trimming'"
          ref="form"
          test-id="instant_quote_bush_trimming_form"
          :current-instant-quote="selectedService"
        />
        <OnboardingInstantQuoteLeafRemovalForm
          v-else-if="isInstantQuoteLeafRemoval"
          ref="form"
          test-id="instant_quote_leaf_removal_form"
          :current-instant-quote="selectedService"
        />
        <OnboardingCleanupForm v-else-if="isCleanup" ref="form" test-id="cleanup_form" />
        <OnboardingFlowerBedWeedingForm
          v-else-if="isFlowerBedWeeding"
          ref="form"
          test-id="bed_weeding_form"
        />
        <OnboardingMulchingForm v-else-if="isMulching" ref="form" test-id="mulching_form" />
        <OnboardingGenericServiceForm v-else ref="form" />
      </div>
      <div
        v-if="!isInstantQuote || selectedService.instant_quote_status === 'customer.info'"
        class="column"
        style="display: flex;"
      >
        <LsButton
          style="margin-left: auto;"
          :text="t('onboarding.skipQuote')"
          track-id="onboarding_services_view-remove_service"
          test-id="remove_service"
          :disabled="onboarding.loadingStatus.isUpdating"
          bg-color="primary-light"
          text-color="primary"
          @click="removeService"
        />
      </div>
    </div>
    <div class="columns">
      <div v-if="showButtons" class="column">
        <LsButton
          v-if="isInstantQuote"
          :text="t('onboarding.generateQuote')"
          track-id="onboarding_services_view-generate_instant_quote"
          test-id="generate_instant_quote"
          :loading="onboarding.loadingStatus.isUpdating"
          @click="generateInstantQuote"
        />

        <LsButton
          v-else
          :text="t('onboarding.saveAndContinue')"
          track-id="onboarding_services_view-save_and_continue"
          :loading="onboarding.loadingStatus.isUpdating"
          type="submit"
          test-id="save_and_continue"
        />
      </div>
      <div v-else class="column">
        <OnboardingInstantQuoteGeneratedQuote ref="form" :current-instant-quote="selectedService" />
      </div>
    </div>
  </form>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import { LsButton, LsClickable } from '@/components/common';
import {
  AUTH__PROCESSING_ORDER,
  ONBOARDING__SELECTED_SERVICE,
  FEATURE_FLAGS__REGISTER_EXPOSURE,
} from '@/store/types';
import _ from 'lodash';
import { lsErrorService, lsModalService, lsRouterHelpers } from 'ls-vue-common';
import { RemoveServiceModal, HowQuotingWorksModal } from '@/modals';
import { LsTypicalPricing } from '@/components';
import OnboardingCleanupForm from './OnboardingCleanupForm/OnboardingCleanupForm.vue';
import OnboardingFlowerBedWeedingForm from './OnboardingFlowerBedWeedingForm/OnboardingFlowerBedWeedingForm.vue';
import OnboardingMulchingForm from './OnboardingMulchingForm/OnboardingMulchingForm.vue';
import OnboardingGenericServiceForm from './OnboardingGenericServiceForm/OnboardingGenericServiceForm.vue';
import OnboardingInstantQuoteBushTrimmingForm from './OnboardingInstantQuoteBushTrimmingForm/OnboardingInstantQuoteBushTrimmingForm.vue';
import OnboardingInstantQuoteLeafRemovalForm from './OnboardingInstantQuoteLeafRemovalForm/OnboardingInstantQuoteLeafRemovalForm.vue';
import OnboardingInstantQuoteGeneratedQuote from './components/OnboardingInstantQuoteGeneratedQuote.vue';

export default {
  name: 'OnboardingServicesView',
  components: {
    LsButton,
    LsClickable,
    LsTypicalPricing,
    OnboardingCleanupForm,
    OnboardingFlowerBedWeedingForm,
    OnboardingMulchingForm,
    OnboardingInstantQuoteBushTrimmingForm,
    OnboardingInstantQuoteLeafRemovalForm,
    OnboardingInstantQuoteGeneratedQuote,
    OnboardingGenericServiceForm,
  },
  data() {
    return {
      service_id: lsRouterHelpers.getParam(this.$route, 'service_id'),
    };
  },
  computed: {
    isLoading() {
      return _.isNil(this.selectedService);
    },
    ...mapState(['onboarding', 'featureFlags']),
    ...mapGetters([AUTH__PROCESSING_ORDER, ONBOARDING__SELECTED_SERVICE]),
    marketID() {
      return this[AUTH__PROCESSING_ORDER].market_id;
    },
    showPriceRange() {
      return _.get(this.featureFlags.flags, ['customer.manualquotes.show_pricerange']);
    },
    selectedService() {
      return this[ONBOARDING__SELECTED_SERVICE](this.service_id);
    },
    isInstantQuote() {
      return _.includes(_.get(this.selectedService, 'type'), 'instant_quote');
    },
    serviceName() {
      if (this.isInstantQuote) {
        const { type } = this.selectedService;

        return this.selectedService[type].service.name;
      }

      return (
        _.get(this.selectedService, 'price.service.name') ||
        _.get(this.selectedService, 'service.name')
      );
    },
    isInstantQuoteLeafRemoval() {
      return (
        this.isInstantQuote &&
        this.selectedService[this.selectedService.type].service.str_id === 'cleanup_leaves'
      );
    },
    isCleanup() {
      return (
        Boolean(_.get(this.selectedService, 'service.str_id') === 'cleanup_spring') &&
        _.get(this.featureFlags.flags, ['customer.manualquotes.questions.cleanup'])
      );
    },
    isFlowerBedWeeding() {
      return Boolean(_.get(this.selectedService, 'service.str_id') === 'bed_weeding');
    },
    isMulching() {
      return Boolean(_.get(this.selectedService, 'service.str_id') === 'mulching');
    },
    showButtons() {
      return (
        !this.isInstantQuote ||
        (this.isInstantQuote && this.selectedService.instant_quote_status === 'customer.info')
      );
    },
  },
  async created() {
    this[FEATURE_FLAGS__REGISTER_EXPOSURE]({ flag: 'customer.manualquotes.show_pricerange' });
  },
  methods: {
    ...mapActions([FEATURE_FLAGS__REGISTER_EXPOSURE]),
    generateInstantQuote() {
      if (_.isFunction(_.get(this.$refs, 'form.generateInstantQuote'))) {
        this.$refs.form.generateInstantQuote();
      } else {
        lsErrorService.log({
          error: {
            message: `this.$refs.form.generateInstantQuote is not a function`,
          },
        });
      }
    },
    updateQuote() {
      if (_.isFunction(_.get(this.$refs, 'form.updateQuote'))) {
        this.$refs.form.updateQuote();
      } else {
        lsErrorService.log({
          error: {
            message: `this.$refs.form.updateQuote is not a function`,
          },
        });
      }
    },
    removeService() {
      lsModalService.open({
        parent: this,
        component: RemoveServiceModal,
        props: {
          orderquoteId: this.selectedService.orderquote_id,
          instantQuoteId: this.isInstantQuote ? _.get(this.selectedService, 'id') : null,
        },
      });
    },
    howQuotingWorks() {
      lsModalService.open({
        parent: this,
        component: HowQuotingWorksModal,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.onboarding-services-view--form {
  border-bottom: 1px solid $primary-light;
  padding-bottom: 35px;
  margin-bottom: 35px;
}

.onboarding-services-view--instant-quote-header {
  padding-bottom: 20px;
  display: flex;
}

.onboarding-services-view--instant-quote-name {
  font-weight: bold;
  padding-left: 6px;
}

.onboarding-services-view--h2 {
  font-weight: bold;
}

.ls-form-header {
  margin-bottom: 30px;

  .ls-typical-pricing {
    margin: 5px 0;
  }

  .ls-how-quoting-works {
    padding-top: 4px;
    display: flex;
    align-items: center;
    font-weight: bold;
    font-size: $size-5;
    color: $blue;

    .ls-question-circle {
      margin-right: 8px;
      color: $white;
      height: 26px;
      width: 26px;
      background: $blue;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
    }

    &:hover {
      color: $gray-primary;
    }
  }
}

@media (max-width: $mobile-max-width) {
  .onboarding-services-view--charge-info {
    width: 100%;
  }

  .onboarding-services-view--instant-quote-header {
    display: flex;
    flex-direction: column;
  }

  .onboarding-services-view--instant-quote-name {
    padding-left: 0px;
  }
}
.temed-app {
  .onboarding-services-view--form {
    border-color: $theme-color-primary-light;
  }
  .ls-form-header {
    .ls-how-quoting-works {
      font-size: $theme-txt-body;
      color: $theme-color-blue;

      .ls-question-circle {
        color: $theme-color-white;
        background: $theme-color-blue;
      }

      &:hover {
        color: $theme-color-gray-dark;
      }
    }
  }
}
</style>
